import { Editor } from "@tiptap/react";
import MenuBarItem from "./menu-bar-item";
import { Icon } from "@elaniintc/goes-design-system";

type EditorOption = {
  icon: React.ReactNode;
  onClick: () => boolean;
  pressed: boolean;
};

interface ManuBarProps {
  editor: Editor | null;
  isDisabled?: boolean;
}
export default function MenuBar({ editor, isDisabled = false }: ManuBarProps) {
  if (!editor) return null;

  const indent = (): boolean => {
    const { state, view } = editor;
    const { from, to } = state.selection;
    const tr = state.tr;

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === "paragraph") {
        // Verificar si el párrafo está dentro de una lista usando depth
        const $pos = state.doc.resolve(pos);
        let isInList = false;

        // Recorrer hacia arriba en el árbol de nodos para buscar listas
        for (let depth = $pos.depth; depth > 0; depth--) {
          const ancestor = $pos.node(depth);
          if (
            ancestor.type.name === "bulletList" ||
            ancestor.type.name === "orderedList"
          ) {
            isInList = true;
            break;
          }
        }

        // Solo indentar párrafos que NO estén dentro de listas
        if (!isInList) {
          const current = node.attrs.indentLevel || 0;
          const newLevel = current + 1;
          tr.setNodeMarkup(pos, node.type, {
            ...node.attrs,
            indentLevel: newLevel,
          });
        }
      } else if (["bulletList", "orderedList"].includes(node.type.name)) {
        // Indentar la lista completa
        const current = node.attrs.indentLevel || 0;
        const newLevel = current + 1;
        tr.setNodeMarkup(pos, node.type, {
          ...node.attrs,
          indentLevel: newLevel,
        });
      }
    });

    if (tr.docChanged) {
      view.dispatch(tr);
    }

    return true;
  };

  const outdent = (): boolean => {
    const { state, view } = editor;
    const { from, to } = state.selection;
    const tr = state.tr;

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === "paragraph") {
        // Verificar si el párrafo está dentro de una lista usando depth
        const $pos = state.doc.resolve(pos);
        let isInList = false;

        // Recorrer hacia arriba en el árbol de nodos para buscar listas
        for (let depth = $pos.depth; depth > 0; depth--) {
          const ancestor = $pos.node(depth);
          if (
            ancestor.type.name === "bulletList" ||
            ancestor.type.name === "orderedList"
          ) {
            isInList = true;
            break;
          }
        }

        // Solo aplicar outdent a párrafos que NO estén dentro de listas
        if (!isInList) {
          const current = node.attrs.indentLevel || 0;
          const newLevel = Math.max(current - 1, 0);
          tr.setNodeMarkup(pos, node.type, {
            ...node.attrs,
            indentLevel: newLevel,
          });
        }
      } else if (["bulletList", "orderedList"].includes(node.type.name)) {
        // Hacer outdent de la lista completa
        const current = node.attrs.indentLevel || 0;
        const newLevel = Math.max(current - 1, 0);
        tr.setNodeMarkup(pos, node.type, {
          ...node.attrs,
          indentLevel: newLevel,
        });
      }
    });

    if (tr.docChanged) {
      view.dispatch(tr);
    }

    return true;
  };

  const headingOptions: EditorOption[] = [
    {
      icon: (
        <Icon
          type="far"
          name="h1"
          className="scale-110 text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      pressed: editor.isActive("heading", { level: 1 }),
    },
    {
      icon: (
        <Icon
          type="far"
          name="h2"
          className="scale-120 text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      pressed: editor.isActive("heading", { level: 2 }),
    },
    {
      icon: (
        <Icon
          type="far"
          name="h3"
          className="scale-120 text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      pressed: editor.isActive("heading", { level: 3 }),
    },
  ];

  const textOptions: EditorOption[] = [
    {
      icon: (
        <Icon
          type="far"
          name="bold"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBold().run(),
      pressed: editor.isActive("bold"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="italic"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleItalic().run(),
      pressed: editor.isActive("italic"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="strikethrough"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleStrike().run(),
      pressed: editor.isActive("strike"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="underline"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleUnderline().run(),
      pressed: editor.isActive("underline"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="link"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => {
        if (editor.isActive("link")) {
          return editor.chain().focus().unsetLink().run();
        }

        const url = window.prompt("URL:");
        if (!url) return false;

        return editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url })
          .run();
      },
      pressed: editor.isActive("link"),
    },
  ];

  const listOptions: EditorOption[] = [
    {
      icon: (
        <Icon
          type="far"
          name="list"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      pressed: editor.isActive("bulletList"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="list-ol"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      pressed: editor.isActive("orderedList"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="block-quote"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBlockquote().run(),
      pressed: editor.isActive("blockquote"),
    },
    {
      icon: (
        <Icon
          type="far"
          name="indent"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().command(indent).run(),
      pressed: false,
    },
    {
      icon: (
        <Icon
          type="far"
          name="indent"
          className="text-sm font-normal text-body transform rotate-180"
        />
      ),
      onClick: () => editor.chain().focus().command(outdent).run(),
      pressed: false,
    },
  ];

  const alignOptions: EditorOption[] = [
    {
      icon: (
        <Icon
          type="far"
          name="align-left"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().setTextAlign("left").run(),
      pressed: editor.isActive({ textAlign: "left" }),
    },
    {
      icon: (
        <Icon
          type="far"
          name="align-center"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().setTextAlign("center").run(),
      pressed: editor.isActive({ textAlign: "center" }),
    },
    {
      icon: (
        <Icon
          type="far"
          name="align-right"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().setTextAlign("right").run(),
      pressed: editor.isActive({ textAlign: "right" }),
    },
    {
      icon: (
        <Icon
          type="far"
          name="align-justify"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().setTextAlign("justify").run(),
      pressed: editor.isActive({ textAlign: "justify" }),
    },
  ];

  const renderGroup = (options: EditorOption[], id: string = "") => (
    <div className="flex shadow-custom" data-testid={id}>
      {options.map((option, index) => (
        <MenuBarItem
          key={index}
          type={
            index === 0
              ? "first"
              : index === options.length - 1
                ? "last"
                : undefined
          }
          isPressed={option.pressed}
          onClick={option.onClick}
          icon={option.icon}
          isDisabled={isDisabled}
        />
      ))}
    </div>
  );

  return (
    <div className="flex flex-col items-center lg:flex-row gap-4">
      {renderGroup(headingOptions, "heading-options-id")}
      {renderGroup(textOptions, "text-options-id")}
      {renderGroup(listOptions, "text-list-id")}
      {renderGroup(alignOptions, "text-align-id")}
      {/* {renderGroup(indentOptions)} */}
    </div>
  );
}
